import { useState } from 'react';
import { Plus, Trash2, RefreshCw, Pencil } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Label } from '@/components/ui/Input';
import { StatusBadge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Dialog } from '@/components/ui/Dialog';
import { useVms, useVmActions } from '@/hooks/useDispatcher';
import { formatDateTime, maskSecret } from '@/lib/utils';
import type { Vm } from '@/types/api';
import { VmFormDialog } from './VmFormDialog';

export default function VmsSettingsPage() {
  const vms = useVms();
  const { remove, rotateToken } = useVmActions();
  const [showCreate, setShowCreate] = useState(false);
  const [editVm, setEditVm] = useState<Vm | null>(null);
  const [rotateFor, setRotateFor] = useState<Vm | null>(null);
  const [newToken, setNewToken] = useState('');

  async function handleDelete(vm: Vm) {
    if (!confirm(`Supprimer la VM "${vm.slug}" ?`)) return;
    await remove.mutateAsync(vm.id);
    toast.success('VM supprimée');
  }

  async function handleRotate() {
    if (!rotateFor || !newToken.trim()) return;
    await rotateToken.mutateAsync({ id: rotateFor.id, token: newToken.trim() });
    setRotateFor(null);
    setNewToken('');
    toast.success('Token rotaté');
  }

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button onClick={() => setShowCreate(true)}>
          <Plus size={14} /> Ajouter une VM
        </Button>
      </div>

      <Card>
        <CardHeader title="VMs LeadBridge" subtitle="Pool d'enrichissement" />
        {vms.isLoading ? (
          <div className="p-5">
            <Skeleton className="h-32 w-full" />
          </div>
        ) : !vms.data?.length ? (
          <EmptyState title="Aucune VM enregistrée" />
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-brand-border">
              <tr>
                <th className="th">Slug</th>
                <th className="th">URL</th>
                <th className="th">Région</th>
                <th className="th">Charge</th>
                <th className="th">Statut</th>
                <th className="th">Vu</th>
                <th className="th">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-border">
              {vms.data.map((vm) => (
                <tr key={vm.id}>
                  <td className="td font-medium text-brand-dark">{vm.slug}</td>
                  <td className="td font-mono text-xs">{maskSecret(vm.base_url, 24)}</td>
                  <td className="td">{vm.region ?? '—'}</td>
                  <td className="td">
                    {vm.active_jobs}/{vm.max_concurrent}
                  </td>
                  <td className="td">
                    <StatusBadge status={vm.status} />
                  </td>
                  <td className="td">{formatDateTime(vm.last_seen_at)}</td>
                  <td className="td">
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setEditVm(vm)}>
                        <Pencil size={12} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setRotateFor(vm)}>
                        <RefreshCw size={12} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(vm)}>
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <VmFormDialog open={showCreate} onClose={() => setShowCreate(false)} />
      <VmFormDialog open={!!editVm} onClose={() => setEditVm(null)} vm={editVm ?? undefined} />

      <Dialog
        open={!!rotateFor}
        onClose={() => setRotateFor(null)}
        title={`Rotation token — ${rotateFor?.slug ?? ''}`}
        description="Le nouveau token remplace l'ancien immédiatement."
        footer={
          <>
            <Button variant="ghost" onClick={() => setRotateFor(null)}>
              Annuler
            </Button>
            <Button onClick={handleRotate} loading={rotateToken.isPending} variant="destructive">
              Rotater
            </Button>
          </>
        }
      >
        <Label>Nouveau token</Label>
        <Input
          type="password"
          autoComplete="off"
          value={newToken}
          onChange={(e) => setNewToken(e.target.value)}
          placeholder="nouveau leadbridge token"
        />
      </Dialog>
    </div>
  );
}
